import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary, input, label } from '../styles/theme.js'
import { uploads as uploadsApi, projects as projectsApi } from '../lib/api.js'

const container = css`
  max-width: 800px;
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const title = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.xl};
`

const stepLabel = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${space.sm};
`

const dropzone = css`
  ${card};
  border: 2px dashed ${colors.border};
  text-align: center;
  padding: ${space['2xl']};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${colors.brand};
    background: ${colors.brandLight};
  }
`

const dropzoneActive = css`
  border-color: ${colors.brand};
  background: ${colors.brandLight};
`

const dropzoneLabel = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.medium};
  margin-bottom: ${space.xs};
`

const dropzoneHint = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

const previewCard = css`
  ${card};
  margin-bottom: ${space.lg};
`

const previewTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const tableWrap = css`
  overflow-x: auto;
  margin-bottom: ${space.md};
`

const table = css`
  width: 100%;
  border-collapse: collapse;
  font-size: ${font.size.sm};
  th, td {
    padding: ${space.sm} ${space.md};
    text-align: left;
    border-bottom: 1px solid ${colors.border};
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  th {
    font-weight: ${font.weight.medium};
    color: ${colors.textSecondary};
    background: ${colors.bg};
  }
`

const mappingSection = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${space.md};
  margin-bottom: ${space.lg};
`

const fieldGroup = css`
  margin-bottom: ${space.md};
`

const selectStyle = css`
  ${input};
  appearance: auto;
`

const detectedBadge = css`
  display: inline-block;
  font-size: ${font.size.xs};
  color: ${colors.teal};
  margin-left: ${space.sm};
`

const actions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const successCard = css`
  ${card};
  text-align: center;
  padding: ${space['2xl']};
  border-color: ${colors.green};
`

const errorMsg = css`
  color: ${colors.critical};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
`

const guidanceSection = css`
  margin-top: ${space.xl};
`

const guidanceTitle = css`
  font-size: ${font.size.base};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
  color: ${colors.text};
`

const guidanceBlock = css`
  ${card};
  margin-bottom: ${space.md};
`

const guidanceBlockTitle = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.sm};
  color: ${colors.textSecondary};
`

const codeBlock = css`
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.xs};
  font-family: 'SF Mono', SFMono-Regular, Menlo, monospace;
  white-space: pre;
  overflow-x: auto;
  line-height: 1.6;
  color: ${colors.textSecondary};
`

const guidanceNote = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-top: ${space.sm};
  line-height: 1.5;
`

// Duplicate resolution styles
const dupCard = css`
  ${card};
  border-color: ${colors.important};
  margin-bottom: ${space.lg};
`

const dupTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  color: ${colors.important};
  margin-bottom: ${space.md};
`

const dupOption = css`
  ${card};
  cursor: pointer;
  margin-bottom: ${space.sm};
  transition: border-color 0.15s ease;
  &:hover { border-color: ${colors.brand}; }
`

const dupOptionSelected = css`
  border-color: ${colors.brand};
  background: ${colors.brandLight};
`

const dupOptionTitle = css`
  font-weight: ${font.weight.semibold};
  margin-bottom: 2px;
`

const dupOptionDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

// Steps: 'pick' -> 'preview' -> 'duplicates' -> 'ingesting' -> 'done'
export function UploadView() {
  let project = null
  let step = 'pick'
  let dragging = false
  let error = null
  let uploading = false
  let ingesting = false

  // From upload response
  let preview = null  // { filename, format, r2Key, rowCount, fields, sampleRows, suggestedMapping }

  // User-confirmed mapping
  let mapping = {}

  // Duplicate resolution
  let dupInfo = null  // { duplicateCount, totalSessions }
  let dupChoice = 'skip'  // 'skip', 'overwrite', 'allow'

  // Ingest result
  let result = null

  // DOM ref for hidden file input (must live in closure scope, not inside renderPick)
  let fileInput

  function handleFile(projectId, file) {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['csv', 'json'].includes(ext)) {
      error = 'Only CSV and JSON files are supported'
      m.redraw()
      return
    }
    error = null
    uploading = true
    m.redraw()

    uploadsApi.upload(projectId, file)
      .then((data) => {
        preview = data
        mapping = { ...data.suggestedMapping }
        step = 'preview'
      })
      .catch((err) => { error = err.message })
      .finally(() => { uploading = false; m.redraw() })
  }

  function confirmImport(projectId, duplicateMode) {
    ingesting = true
    error = null
    step = 'ingesting'
    m.redraw()

    uploadsApi.confirm(projectId, {
      r2Key: preview.r2Key,
      filename: preview.filename,
      format: preview.format,
      mapping,
      duplicateMode: duplicateMode || undefined,
    })
      .then((data) => {
        if (data.needsDuplicateResolution) {
          // Server detected duplicates, ask user
          dupInfo = data
          dupChoice = 'skip'
          step = 'duplicates'
        } else {
          result = data
          step = 'done'
        }
      })
      .catch((err) => {
        error = err.message
        step = 'preview'
      })
      .finally(() => { ingesting = false; m.redraw() })
  }

  function confirmDuplicateChoice(projectId) {
    // Re-submit with chosen duplicate mode
    confirmImport(projectId, dupChoice)
  }

  function reset() {
    step = 'pick'
    preview = null
    mapping = {}
    result = null
    error = null
    dupInfo = null
    dupChoice = 'skip'
  }

  function renderPick(projectId) {
    return m('div', [
      m('div', { class: stepLabel }, 'Step 1'),
      m('div', {
        class: `${dropzone} ${dragging ? dropzoneActive : ''}`,
        ondragover: (e) => { e.preventDefault(); dragging = true },
        ondragleave: () => { dragging = false },
        ondrop: (e) => {
          e.preventDefault()
          dragging = false
          const file = e.dataTransfer.files[0]
          handleFile(projectId, file)
        },
        onclick: () => fileInput.click(),
      }, [
        m('div', { class: dropzoneLabel }, uploading ? 'Uploading...' : 'Drop a file here or click to browse'),
        m('div', { class: dropzoneHint }, 'CSV or JSON, up to 50MB'),
        m('input', {
          type: 'file',
          accept: '.csv,.json',
          style: 'display:none',
          oncreate: (vnode) => { fileInput = vnode.dom },
          onchange: (e) => {
            const file = e.target.files[0]
            if (file) handleFile(projectId, file)
            e.target.value = ''
          },
        }),
      ]),
      error ? m('div', { class: errorMsg }, error) : null,

      // Data format guidance
      m('div', { class: guidanceSection }, [
        m('div', { class: guidanceTitle }, 'Accepted formats'),

        m('div', { class: guidanceBlock }, [
          m('div', { class: guidanceBlockTitle }, 'CSV \u2014 one row per session'),
          m('pre', { class: codeBlock },
`session_id,messages,timestamp,tags
sess-1,"[{""role"":""user"",""content"":""What are my rights?""},{""role"":""assistant"",""content"":""You have the right to...""}]",2025-03-01T10:00:00,"housing, tenant-rights"`),
          m('div', { class: guidanceNote },
            'Each row is one session. The messages column contains a JSON array of objects with role and content fields. Wrap the JSON in quotes and escape inner quotes by doubling them (""). Columns for session_id, timestamp, and tags are optional.'),
        ]),

        m('div', { class: guidanceBlock }, [
          m('div', { class: guidanceBlockTitle }, 'JSON \u2014 flat (array of messages)'),
          m('pre', { class: codeBlock },
`[
  { "role": "user", "content": "Hello", "session_id": "s1" },
  { "role": "assistant", "content": "Hi!", "session_id": "s1" }
]`),
          m('div', { class: guidanceNote },
            'Same structure as CSV but in JSON. Each object is one message.'),
        ]),

        m('div', { class: guidanceBlock }, [
          m('div', { class: guidanceBlockTitle }, 'JSON \u2014 nested (array of sessions)'),
          m('pre', { class: codeBlock },
`[
  {
    "id": "sess-1",
    "messages": [
      { "role": "user", "content": "What are my rights?" },
      { "role": "assistant", "content": "You have the right to..." }
    ],
    "tags": ["housing", "tenant-rights"]
  }
]`),
          m('div', { class: guidanceNote },
            'Each object is a full session containing a messages array. Extra fields like id, timestamp, or tags are preserved.'),
        ]),

        m('div', { class: guidanceBlock }, [
          m('div', { class: guidanceBlockTitle }, 'JSON \u2014 OpenAI format'),
          m('pre', { class: codeBlock },
`[
  {
    "id": "chatcmpl-abc",
    "messages": [
      { "role": "user", "content": "Explain eviction rules" },
      { "role": "assistant", "content": "In most states..." }
    ]
  }
]`),
          m('div', { class: guidanceNote },
            'Standard OpenAI chat completion format. Must have a messages array with role/content pairs.'),
        ]),

        m('div', { class: guidanceBlock }, [
          m('div', { class: guidanceBlockTitle }, 'Importing tags'),
          m('div', { class: guidanceNote, style: 'margin-top: 0;' },
            'You can include tags in your data to pre-categorize sessions on import. In CSV, add a column (e.g. "tags") with comma-separated values. In JSON, use an array of strings or a comma-separated string. During the mapping step you\'ll be able to select which field contains your tags. Any tags that don\'t already exist in the project will be created automatically.'),
        ]),
      ]),
    ])
  }

  function renderPreview(projectId) {
    const isNested = mapping.format === 'openai' || mapping.format === 'nested'

    return m('div', [
      m('div', { class: stepLabel }, 'Step 2 \u2014 Review and map fields'),

      m('div', { class: previewCard }, [
        m('div', { class: previewTitle },
          `${preview.filename} \u2014 ${preview.rowCount} ${isNested ? 'sessions' : 'rows'} detected`),

        // Sample data table
        preview.sampleRows.length > 0
          ? m('div', { class: tableWrap }, [
              m('table', { class: table }, [
                m('thead', m('tr', preview.fields.map((f) =>
                  m('th', f)
                ))),
                m('tbody', preview.sampleRows.slice(0, 3).map((row) =>
                  m('tr', preview.fields.map((f) => {
                    const val = row[f]
                    const display = Array.isArray(val)
                      ? `[${val.length} items]`
                      : typeof val === 'object' && val !== null
                        ? JSON.stringify(val).slice(0, 80)
                        : String(val ?? '').slice(0, 100)
                    return m('td', { title: String(val ?? '') }, display)
                  }))
                )),
              ]),
            ])
          : null,
      ]),

      // Field mapping
      m('div', { class: previewCard }, [
        m('div', { class: previewTitle }, 'Field mapping'),

        isNested
          ? renderNestedMapping()
          : renderFlatMapping(),
      ]),

      error ? m('div', { class: errorMsg }, error) : null,

      m('div', { class: actions }, [
        m('button', { class: btnSecondary, onclick: reset }, 'Start over'),
        m('button', {
          class: btnPrimary,
          onclick: () => confirmImport(projectId),
          disabled: !isMappingValid(),
        }, 'Import data'),
      ]),
    ])
  }

  function renderFlatMapping() {
    const fields = preview.fields
    const noneOpt = m('option', { value: '' }, '\u2014 none \u2014')
    const fieldOpts = (selected) =>
      [noneOpt, ...fields.map((f) =>
        m('option', { value: f, selected: f === selected }, f)
      )]

    return m('div', { class: mappingSection }, [
      m('div', { class: fieldGroup }, [
        m('label', { class: label }, [
          'Role field',
          mapping.roleField ? m('span', { class: detectedBadge }, '(detected)') : null,
        ]),
        m('select', {
          class: selectStyle,
          value: mapping.roleField || '',
          onchange: (e) => { mapping.roleField = e.target.value || undefined },
        }, fieldOpts(mapping.roleField)),
      ]),

      m('div', { class: fieldGroup }, [
        m('label', { class: label }, [
          'Content field',
          mapping.contentField ? m('span', { class: detectedBadge }, '(detected)') : null,
        ]),
        m('select', {
          class: selectStyle,
          value: mapping.contentField || '',
          onchange: (e) => { mapping.contentField = e.target.value || undefined },
        }, fieldOpts(mapping.contentField)),
      ]),

      m('div', { class: fieldGroup }, [
        m('label', { class: label }, [
          'Session ID field',
          mapping.sessionIdField ? m('span', { class: detectedBadge }, '(detected)') : null,
        ]),
        m('select', {
          class: selectStyle,
          value: mapping.sessionIdField || '',
          onchange: (e) => { mapping.sessionIdField = e.target.value || undefined },
        }, fieldOpts(mapping.sessionIdField)),
      ]),

      m('div', { class: fieldGroup }, [
        m('label', { class: label }, [
          'Timestamp field',
          mapping.timestampField ? m('span', { class: detectedBadge }, '(detected)') : null,
        ]),
        m('select', {
          class: selectStyle,
          value: mapping.timestampField || '',
          onchange: (e) => { mapping.timestampField = e.target.value || undefined },
        }, fieldOpts(mapping.timestampField)),
      ]),

      m('div', { class: fieldGroup }, [
        m('label', { class: label }, [
          'Tags field',
          mapping.tagsField ? m('span', { class: detectedBadge }, '(detected)') : null,
        ]),
        m('select', {
          class: selectStyle,
          value: mapping.tagsField || '',
          onchange: (e) => { mapping.tagsField = e.target.value || undefined },
        }, fieldOpts(mapping.tagsField)),
      ]),
    ])
  }

  function renderNestedMapping() {
    const fields = preview.fields
    const msgFields = preview.messageFields || []
    const noneOpt = m('option', { value: '' }, '\u2014 none \u2014')
    const fieldOpts = (selected) =>
      [noneOpt, ...fields.map((f) =>
        m('option', { value: f, selected: f === selected }, f)
      )]
    const msgFieldOpts = (selected) =>
      [noneOpt, ...msgFields.map((f) =>
        m('option', { value: f, selected: f === selected }, f)
      )]

    return m('div', [
      m('div', {
        class: css`
          background: ${colors.bgSidebar};
          padding: ${space.md};
          border-radius: 6px;
          margin-bottom: ${space.md};
          font-size: ${font.size.sm};
        `,
      }, mapping.format === 'openai'
        ? 'OpenAI chat format detected. Each object contains a messages array with role/content pairs.'
        : 'Nested format detected. Each object contains an array of messages.'),

      m('div', { class: mappingSection }, [
        m('div', { class: fieldGroup }, [
          m('label', { class: label }, 'Messages array field'),
          m('select', {
            class: selectStyle,
            value: mapping.messagesField || '',
            onchange: (e) => { mapping.messagesField = e.target.value || undefined },
          }, fieldOpts(mapping.messagesField)),
        ]),

        m('div', { class: fieldGroup }, [
          m('label', { class: label }, 'Session ID field'),
          m('select', {
            class: selectStyle,
            value: mapping.sessionIdField || '',
            onchange: (e) => { mapping.sessionIdField = e.target.value || undefined },
          }, fieldOpts(mapping.sessionIdField)),
        ]),

        // Message-level field mapping
        msgFields.length > 0 ? m('div', { class: fieldGroup }, [
          m('label', { class: label }, [
            'Message role field',
            mapping.messageRoleField ? m('span', { class: detectedBadge }, '(detected)') : null,
          ]),
          m('select', {
            class: selectStyle,
            value: mapping.messageRoleField || '',
            onchange: (e) => { mapping.messageRoleField = e.target.value || undefined },
          }, msgFieldOpts(mapping.messageRoleField)),
        ]) : null,

        msgFields.length > 0 ? m('div', { class: fieldGroup }, [
          m('label', { class: label }, [
            'Message content field',
            mapping.messageContentField ? m('span', { class: detectedBadge }, '(detected)') : null,
          ]),
          m('select', {
            class: selectStyle,
            value: mapping.messageContentField || '',
            onchange: (e) => { mapping.messageContentField = e.target.value || undefined },
          }, msgFieldOpts(mapping.messageContentField)),
        ]) : null,

        m('div', { class: fieldGroup }, [
          m('label', { class: label }, 'Session timestamp field'),
          m('select', {
            class: selectStyle,
            value: mapping.sessionTimestampField || '',
            onchange: (e) => { mapping.sessionTimestampField = e.target.value || undefined },
          }, fieldOpts(mapping.sessionTimestampField)),
        ]),

        m('div', { class: fieldGroup }, [
          m('label', { class: label }, [
            'Tags field',
            mapping.tagsField ? m('span', { class: detectedBadge }, '(detected)') : null,
          ]),
          m('select', {
            class: selectStyle,
            value: mapping.tagsField || '',
            onchange: (e) => { mapping.tagsField = e.target.value || undefined },
          }, fieldOpts(mapping.tagsField)),
        ]),
      ]),
    ])
  }

  function isMappingValid() {
    if (mapping.format === 'openai' || mapping.format === 'nested') {
      return !!mapping.messagesField && !!mapping.messageContentField
    }
    return !!mapping.roleField && !!mapping.contentField
  }

  function renderDuplicates(projectId) {
    return m('div', [
      m('div', { class: dupCard }, [
        m('div', { class: dupTitle },
          `${dupInfo.duplicateCount} of ${dupInfo.totalSessions} sessions already exist`),
        m('div', {
          style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; margin-bottom: ${space.lg};`,
        }, 'Sessions with the same ID were found in this project. How should duplicates be handled?'),

        // Options
        m('div', {
          class: `${dupOption} ${dupChoice === 'skip' ? dupOptionSelected : ''}`,
          onclick: () => { dupChoice = 'skip' },
        }, [
          m('div', { class: dupOptionTitle }, 'Skip duplicates (recommended)'),
          m('div', { class: dupOptionDesc }, 'Only import new sessions. Existing sessions are left unchanged.'),
        ]),

        m('div', {
          class: `${dupOption} ${dupChoice === 'overwrite' ? dupOptionSelected : ''}`,
          onclick: () => { dupChoice = 'overwrite' },
        }, [
          m('div', { class: dupOptionTitle }, 'Overwrite duplicates'),
          m('div', { class: dupOptionDesc }, 'Replace messages and tags on existing sessions with the new data.'),
        ]),

        m('div', {
          class: `${dupOption} ${dupChoice === 'allow' ? dupOptionSelected : ''}`,
          onclick: () => { dupChoice = 'allow' },
        }, [
          m('div', { class: dupOptionTitle }, 'Import all (creates duplicates)'),
          m('div', { class: dupOptionDesc }, 'Import everything as new sessions, even if the same IDs already exist.'),
        ]),
      ]),

      error ? m('div', { class: errorMsg }, error) : null,

      m('div', { class: actions }, [
        m('button', { class: btnSecondary, onclick: reset }, 'Cancel'),
        m('button', {
          class: btnPrimary,
          onclick: () => confirmDuplicateChoice(projectId),
        }, 'Continue import'),
      ]),
    ])
  }

  function renderIngesting() {
    return m('div', { class: previewCard, style: 'text-align:center; padding:3rem;' }, [
      m('div', { class: css`font-size: ${font.size.lg}; font-weight: ${font.weight.medium};` },
        'Importing data...'),
      m('div', { class: css`color: ${colors.textMuted}; margin-top: ${space.sm};` },
        'This may take a moment for large datasets.'),
    ])
  }

  function renderDone(projectId) {
    return m('div', { class: successCard }, [
      m('div', { class: css`font-size: ${font.size['2xl']}; font-weight: ${font.weight.bold}; color: ${colors.green}; margin-bottom: ${space.md};` },
        'Import complete'),
      m('div', { class: css`font-size: ${font.size.lg}; margin-bottom: ${space.xs};` },
        `${result.sessionCount} sessions`),
      m('div', { class: css`color: ${colors.textSecondary}; margin-bottom: ${space.lg};` }, [
        `${result.messageCount} messages imported`,
        result.tagsCreated
          ? m('div', { style: `margin-top: ${space.xs};` },
              `${result.tagsCreated} new tag${result.tagsCreated === 1 ? '' : 's'} created, ${result.sessionsTagged} session${result.sessionsTagged === 1 ? '' : 's'} tagged`)
          : result.sessionsTagged
            ? m('div', { style: `margin-top: ${space.xs};` },
                `${result.sessionsTagged} session${result.sessionsTagged === 1 ? '' : 's'} tagged`)
            : null,
        result.sessionsSkipped
          ? m('div', { style: `margin-top: ${space.xs};` },
              `${result.sessionsSkipped} duplicate session${result.sessionsSkipped === 1 ? '' : 's'} skipped`)
          : null,
        result.sessionsOverwritten
          ? m('div', { style: `margin-top: ${space.xs};` },
              `${result.sessionsOverwritten} session${result.sessionsOverwritten === 1 ? '' : 's'} overwritten`)
          : null,
      ]),
      m('div', { class: css`display: flex; gap: ${space.sm}; justify-content: center;` }, [
        m('button', { class: btnSecondary, onclick: reset }, 'Upload more'),
        m(m.route.Link, {
          href: `/projects/${projectId}/browser`,
          class: btnPrimary,
        }, 'Browse sessions'),
      ]),
    ])
  }

  return {
    async oninit(vnode) {
      const projectId = vnode.attrs.projectId
      try {
        const proj = await projectsApi.get(projectId)
        project = proj
        if (proj.memberRole !== 'owner') {
          m.route.set(`/projects/${projectId}`)
        }
      } catch { /* auth will catch */ }
    },
    view(vnode) {
      const projectId = vnode.attrs.projectId

      return m('div', { class: container }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Upload data',
        ]),

        m('h1', { class: title }, 'Upload data'),

        step === 'pick' ? renderPick(projectId) : null,
        step === 'preview' ? renderPreview(projectId) : null,
        step === 'duplicates' ? renderDuplicates(projectId) : null,
        step === 'ingesting' ? renderIngesting() : null,
        step === 'done' ? renderDone(projectId) : null,
      ])
    },
  }
}
